import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { BehaviorSubject, Subscription, take } from "rxjs";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";

import { VmManager } from "@onsip/common/services/api/resources/vmManager/vm-manager";
import { VoicemailManagementService } from "@onsip/common/services/api/resources/vmManager/vm-manager.service";
import { views } from "@onsip/web/app/phone/views";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
@Component({
  selector: "onsip-voicemail-manager-edit",
  templateUrl: "./voicemail-manager-edit.component.html",
  styleUrls: ["./voicemail-manager-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class VoicemailManagerEditComponent implements OnInit, OnDestroy {
  @Input() voicemailManagement!: VmManager;
  @Output() editPanel = new EventEmitter<void>();
  protected unsubscriber = new Subscription();

  form = this.formBuilder.group({
    username: ["", Validators.required],
    extensions: [<Array<string>>[]]
  });

  addressId$ = new BehaviorSubject("");
  loadingStateComplete$ = new BehaviorSubject(true);

  views = views;

  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private vmManagerService: VoicemailManagementService,
    private translate: TranslateService,
    private snackbar: SnackbarService,
    public dialog: MatDialog,
    private addressSummaryService: AddressSummaryService
  ) {}

  ngOnInit(): void {
    this.fillForm();
  }

  get extensions(): Array<string> {
    return this.form.controls.extensions.value;
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  submitForm(): void {
    if (this.form.invalid) {
      return;
    }
    this.loadingStateComplete$.next(false);
    this.vmManagerService
      .voicemailManagementAddressEdit({
        Address: this.voicemailManagement.aor,
        Username: this.form.controls.username.value as string
      })
      .then(response => {
        if (response.status === "success") {
          this.extensionsListRef.editExtensions(this.voicemailManagement.username);
          this.addressSummaryService.addressSummaryBrowse();
          this.snackbar.openSnackBar(
            this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
            "success"
          );
          this.editPanel.emit();
        } else {
          this.snackbar.openSnackBar(response.data.message, "error");
          this.vmManagerService.clearErrors();
          return;
        }
        this.loadingStateComplete$.next(true);
      });
  }

  cancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj?.doPrimaryAction) {
            this.editPanel.emit();
          }
        })
    );
  }

  private fillForm() {
    this.form.patchValue({
      username: this.voicemailManagement?.username
    });
    this.addressId$.next(this.voicemailManagement?.addressId);
  }
}
